import { type ComponentProps } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react';

import { cn } from '@/util/utils';

/**
 * ui-pagination: Previous, numbered pages, an ellipsis, Next, all on the 36px control
 * height, 44 on touch. The current page sits on the card with a hairline and shadow/xs; the rest are
 * plain until hovered.
 */
function Pagination({ className, ref, ...props }: ComponentProps<'nav'>) {
    return (
        <nav
            role="navigation"
            aria-label="Pagination"
            data-slot="pagination"
            className={cn('flex', className)}
            ref={ref}
            {...props}
        />
    );
}

function PaginationContent({ className, ref, ...props }: ComponentProps<'ul'>) {
    return (
        <ul
            data-slot="pagination-content"
            className={cn('flex flex-row items-center gap-1', className)}
            ref={ref}
            {...props}
        />
    );
}

function PaginationItem({ ref, ...props }: ComponentProps<'li'>) {
    return <li data-slot="pagination-item" ref={ref} {...props} />;
}

type PaginationLinkProps = ComponentProps<'button'> & {
    isActive?: boolean;
};

const linkClasses =
    'text-foreground inline-flex h-9 min-w-9 pointer-coarse:h-11 pointer-coarse:min-w-11 items-center justify-center gap-1 rounded-md border border-transparent px-2 text-sm hover:bg-muted transition-colors duration-(--duration-fast) outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent [&_svg]:size-4';

/** A page. A button, because paging is an action on the list, not a navigation. */
function PaginationLink({ className, isActive, ref, ...props }: PaginationLinkProps) {
    return (
        <button
            type="button"
            aria-current={isActive ? 'page' : undefined}
            data-slot="pagination-link"
            className={cn(linkClasses, isActive && 'bg-card border-border shadow-xs', className)}
            ref={ref}
            {...props}
        />
    );
}

function PaginationPrevious({ className, ref, ...props }: ComponentProps<'button'>) {
    return (
        <button
            type="button"
            aria-label="Previous page"
            data-slot="pagination-previous"
            className={cn(linkClasses, 'pl-1.5', className)}
            ref={ref}
            {...props}
        >
            <ChevronLeftIcon />
            <span className="hidden sm:inline">Previous</span>
        </button>
    );
}

function PaginationNext({ className, ref, ...props }: ComponentProps<'button'>) {
    return (
        <button
            type="button"
            aria-label="Next page"
            data-slot="pagination-next"
            className={cn(linkClasses, 'pr-1.5', className)}
            ref={ref}
            {...props}
        >
            <span className="hidden sm:inline">Next</span>
            <ChevronRightIcon />
        </button>
    );
}

function PaginationEllipsis({ className, ref, ...props }: ComponentProps<'span'>) {
    return (
        <span
            aria-hidden
            data-slot="pagination-ellipsis"
            className={cn(
                'text-muted-foreground flex size-9 items-center justify-center pointer-coarse:size-11',
                className,
            )}
            ref={ref}
            {...props}
        >
            <MoreHorizontalIcon className="size-4" />
        </span>
    );
}

export {
    Pagination,
    PaginationContent,
    PaginationLink,
    PaginationItem,
    PaginationPrevious,
    PaginationNext,
    PaginationEllipsis,
};
